// The pawn facing chevron (PLAN §5.7.4).
//
// The carved pawn is a body of revolution, so a rotated one looks identical to
// an unrotated one — that art physically cannot express facing, which is a
// load-bearing rule. A chevron on the base breaks the symmetry on the one
// piece type that needs it. The walker pawn has a front of its own, and the
// chevron stays as the one mark of facing that is the same colour and shape
// on every set and on a belt's deck.
//
// Built procedurally rather than modelled: it keeps the asset pipeline to
// "convert and fix defects", leaves the piece meshes untouched, and makes
// size, colour and offset tunable while we find out what actually reads.
//
// It also buys the animation for free: rotating the pawn's group turns the
// chevron with it, so a knight's re-aim (Phase 3) is simply a spin of the
// group.

import { type BufferGeometry, ExtrudeGeometry, Shape } from "three";

// A base-level marker pointing *away* from the camera is geometrically
// impossible to see at any readable pitch, and that is worth writing down
// because it is not obvious until you render it.
//
// A marker at radius r on the floor projects up-screen by r*sin(pitch); the
// piece above it covers up-screen by h*cos(pitch). So the marker only clears
// its own piece when r > h/tan(pitch). For a pawn (h = 0.8 after PIECE_SCALE)
// at 47 degrees that needs r > 0.75 — well outside the square.
//
// Measured, not guessed: the first render had every Black chevron legible and
// every White one hidden, because White's point away from the viewer.
//
// While the carved set was the only set, the fix was to draw the chevron on
// top of everything, depth test off, rather than to move it off the base,
// which is where the design wants it. The walker pawn shows its own front, so
// the chevron now draws where it lies (`chevronMaterial` in pieceViews.ts)
// and one pointing away from the camera is hidden by its pawn. That is the
// trade, made knowingly: a mark drawn through the piece in front of it read
// as a bug once the pieces had faces.

/** Fractions of a square. The pawn's base is 0.587 wide. */
const WIDTH = 0.26;
const DEPTH = 0.17;
const THICKNESS = 0.035;
/** Distance from the piece's centre to the chevron's tip. */
const RADIUS = 0.34;

function chevronShape(): Shape {
  const shape = new Shape();
  const halfWidth = WIDTH / 2;
  const notch = DEPTH * 0.45;

  // ExtrudeGeometry builds in XY and extrudes along +Z; the rotateX(-PI/2)
  // below maps (x, y, z) -> (x, z, -y). So shape +Y becomes world -Z, and the
  // tip belongs at +DEPTH/2 to point north. Getting this backwards is exactly
  // the bug that shipped first: the chevron sat north of the pawn but pointed
  // back at it.
  shape.moveTo(0, DEPTH / 2);
  shape.lineTo(halfWidth, -DEPTH / 2);
  shape.lineTo(0, -DEPTH / 2 + notch);
  shape.lineTo(-halfWidth, -DEPTH / 2);
  shape.closePath();
  return shape;
}

/**
 * Chevron geometry in the pawn's local space: lying flat on the board, tip
 * toward -Z, lifted a hair off the tile so it never z-fights the square.
 *
 * `radius` moves it out from the centre of its square (the pawn wants it on
 * the base, in front of the piece); `scale` sizes it. Belt parts reuse this
 * shape centred and larger — Kenney's conveyor is mirror-symmetric along its
 * own run, so like the pawn it cannot show which way it points on its own.
 */
export function createChevronGeometry(
  { radius = RADIUS, scale = 1 }: { radius?: number; scale?: number } = {}
): BufferGeometry {
  const geometry = new ExtrudeGeometry(chevronShape(), {
    depth: THICKNESS,
    bevelEnabled: false
  });

  // Extrude builds along +Z; lie it flat so its thickness runs along +Y.
  geometry.rotateX(-Math.PI / 2);
  if (scale !== 1) geometry.scale(scale, 1, scale);
  geometry.translate(0, 0.012, -radius);
  geometry.computeVertexNormals();
  return geometry;
}
